import { Box, ButtonBase, FormControlLabel, InputAdornment, Switch, TextField } from '@mui/material';
import { Category, LocalizedString } from '@synergy/models';
import { CloseOutlined, FilterListOutlined, SearchOutlined } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolButton, PoolModal } from '@synergy/components';
import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { getLocalizedString, unique } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';

import { InquiryDeliveryRegion, asksForDistillates, deliveryRegionsParam } from '../utils/inquiry-supplier-groups';
import { PoolCategoryTree } from '../../../common/components/pool-category-tree/pool-category-tree';
import { currentlyLoadedCategoriesSelector } from '../../../common/selectors/category-selectors';
import { localesSelector } from '../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';

const c = tokens.color;

/** One product group of the positions, however often its name stands in the category tree. */
export interface InquiryCategoryGroup {
    name: string;
    ids: number[];
}

/**
 * The positions' product groups, one per name.
 *
 * The same name stands in several places of the category tree, and listed by
 * id the filter showed "Wälzlager, Gleitlager, Gelenklager" six times on three
 * lines (Anfrage 32668). One chip per name carries all its ids, so the search
 * still finds every supplier of every variant (#2258).
 */
export const groupCategoriesByName = (
    categories: Category[],
    nameOf: (value?: LocalizedString) => string
): InquiryCategoryGroup[] => {
    const byName = new Map<string, InquiryCategoryGroup>();

    categories.forEach(category => {
        const name = nameOf(category.translations).trim();
        const group = byName.get(name) ?? { name, ids: [] };

        if (!group.ids.includes(category.id)) {
            group.ids.push(category.id);
        }
        byName.set(name, group);
    });

    return [...byName.values()];
};

/** Chips shown before the rest folds into "+n". */
const VISIBLE_CHIPS = 5;

export interface InquirySupplierSearchBarProps {
    /** Per position its deepest category with suppliers; the filter starts with them. */
    defaultCategories: Category[];
    /** Every category of the positions, parents included, to tell what a chip lies below. */
    knownCategories: Category[];
    /** Where the goods go: distillate suppliers must deliver there (#2322 R-226). */
    deliveryRegions: InquiryDeliveryRegion[];
    searchParams: URLSearchParams;
    onSearch: (searchParams: URLSearchParams) => void;
    /** The groups the filter holds, for "Passt zu". */
    onGroupsChange: (groups: InquiryCategoryGroup[]) => void;
    /** "Neuer Lieferant", for those allowed to create one. */
    action?: ReactNode;
}

/**
 * "Lieferanten hinzufügen" in one line — contract partners only, the filter,
 * the search — and the product groups of the positions in one more (#2258).
 */
export const InquirySupplierSearchBar = ({
    defaultCategories,
    knownCategories,
    deliveryRegions,
    searchParams,
    onSearch,
    onGroupsChange,
    action,
}: InquirySupplierSearchBarProps): ReactElement => {
    const intl = useIntl();
    const locales = useAppSelector(localesSelector);
    const loadedCategories = useAppSelector(currentlyLoadedCategoriesSelector);
    const [selectedIds, setSelectedIds] = useState<number[]>(() =>
        searchParams.has('filter[categoryIds]')
            ? (searchParams.get('filter[categoryIds]') ?? '').split(',').filter(Boolean).map(Number)
            : defaultCategories.map(category => category.id)
    );
    const [search, setSearch] = useState(searchParams.get('search') ?? '');
    const [contractPartnersOnly, setContractPartnersOnly] = useState(!!searchParams.get('filter[isContractPartner]'));
    const [treeOpen, setTreeOpen] = useState(false);
    const [allChips, setAllChips] = useState(false);
    const [partialRegions, setPartialRegions] = useState(!!searchParams.get('filter[deliveryRegionsPartial]'));
    const [withoutRegion, setWithoutRegion] = useState(!!searchParams.get('filter[deliveryRegionsWithoutRegion]'));

    const nameOf = (value?: LocalizedString): string => getLocalizedString(locales, value) ?? '';

    const groups = useMemo(() => {
        const known = unique(loadedCategories.concat(defaultCategories), category => category.id);

        return groupCategoriesByName(
            known.filter(category => selectedIds.includes(category.id)),
            nameOf
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedCategories, defaultCategories, selectedIds, locales]);

    const known = unique(loadedCategories.concat(knownCategories, defaultCategories), category => category.id);
    const isDistillate = deliveryRegions.length > 0 && asksForDistillates(selectedIds, known, nameOf);

    // The search below reads these at the time it runs, not as they were first.
    const regionsRef = useRef({ known, deliveryRegions, nameOf, partialRegions, withoutRegion });
    regionsRef.current = { known, deliveryRegions, nameOf, partialRegions, withoutRegion };

    // Typing waits for a pause; a switch or a chip searches at once.
    const searchNow = useRef((ids: number[], text: string, partnersOnly: boolean) => {
        const regions = regionsRef.current;

        searchParams.set('search', text);
        searchParams.delete('filter[categoryIds]');
        searchParams.delete('filter[isContractPartner]');
        searchParams.delete('filter[deliveryRegions]');
        searchParams.delete('filter[deliveryRegionsPartial]');
        searchParams.delete('filter[deliveryRegionsWithoutRegion]');
        // Only for distillates: elsewhere a supplier delivers wherever it is asked to.
        if (regions.deliveryRegions.length > 0 && asksForDistillates(ids, regions.known, regions.nameOf)) {
            searchParams.set('filter[deliveryRegions]', deliveryRegionsParam(regions.deliveryRegions));
            if (regions.partialRegions) {
                searchParams.set('filter[deliveryRegionsPartial]', 'true');
            }
            if (regions.withoutRegion) {
                searchParams.set('filter[deliveryRegionsWithoutRegion]', 'true');
            }
        }
        // One parameter, the ids comma-separated: repeated, the server kept
        // only the last one, and a filter of seven groups searched for one.
        if (ids.length > 0) {
            searchParams.set('filter[categoryIds]', ids.join(','));
        }
        if (partnersOnly) {
            searchParams.set('filter[isContractPartner]', 'true');
        }
        onSearch(searchParams);
    });
    const searchLater = useMemo(() => debounce(searchNow.current, 600), []);

    useEffect(() => () => searchLater.cancel(), [searchLater]);

    useEffect(() => onGroupsChange(groups), [groups, onGroupsChange]);

    // New positions bring new product groups: the filter starts over with them.
    const defaultKey = defaultCategories.map(category => category.id).join(',');
    const firstKey = useRef(defaultKey);

    useEffect(() => {
        if (firstKey.current === defaultKey) {
            searchNow.current(selectedIds, search, contractPartnersOnly);
            return;
        }
        firstKey.current = defaultKey;
        const ids = defaultCategories.map(category => category.id);
        setSelectedIds(ids);
        searchNow.current(ids, search, contractPartnersOnly);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultKey]);

    // Other customer cards, other regions: the search asks again.
    const regionsKey = deliveryRegionsParam(deliveryRegions);
    const firstRegionsKey = useRef(regionsKey);

    useEffect(() => {
        if (firstRegionsKey.current !== regionsKey) {
            firstRegionsKey.current = regionsKey;
            searchNow.current(selectedIds, search, contractPartnersOnly);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regionsKey]);

    const toggleRegions = (change: { partialRegions?: boolean; withoutRegion?: boolean }): void => {
        regionsRef.current = { ...regionsRef.current, ...change };
        if (undefined !== change.partialRegions) {
            setPartialRegions(change.partialRegions);
        }
        if (undefined !== change.withoutRegion) {
            setWithoutRegion(change.withoutRegion);
        }
        searchNow.current(selectedIds, search, contractPartnersOnly);
    };

    const removeGroup = (group: InquiryCategoryGroup): void => {
        const ids = selectedIds.filter(id => !group.ids.includes(id));
        setSelectedIds(ids);
        searchNow.current(ids, search, contractPartnersOnly);
    };

    const shownGroups = allChips ? groups : groups.slice(0, VISIBLE_CHIPS);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: '8px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <Box
                    sx={{
                        fontSize: '10.5px',
                        fontWeight: 640,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: c.ink3,
                        mr: 'auto',
                    }}
                >
                    <FormattedMessage id="app.inquiries.detail.addSupplier" />
                </Box>
                <FormControlLabel
                    sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12.5px' } }}
                    control={
                        <Switch
                            size="small"
                            checked={contractPartnersOnly}
                            onChange={() => {
                                const next = !contractPartnersOnly;
                                setContractPartnersOnly(next);
                                searchNow.current(selectedIds, search, next);
                            }}
                        />
                    }
                    label={<FormattedMessage id="app.label.contractorsOnly" />}
                />
                <ButtonBase
                    onClick={() => setTreeOpen(true)}
                    sx={{ gap: '4px', fontSize: '12.5px', color: c.ink2, '&:hover': { color: c.ink } }}
                >
                    <FilterListOutlined sx={{ fontSize: 17 }} />
                    <FormattedMessage id="app.common.filter" />
                </ButtonBase>
                <TextField
                    size="small"
                    sx={{ width: 260 }}
                    placeholder={intl.formatMessage({ id: 'app.inquiries.detail.supplierSearchPlaceholder' })}
                    value={search}
                    onChange={event => {
                        setSearch(event.target.value);
                        searchLater(selectedIds, event.target.value, contractPartnersOnly);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlined sx={{ fontSize: 15 }} />
                            </InputAdornment>
                        ),
                    }}
                />
                {action}
            </Box>
            {groups.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '12px' }}>
                    <Box component="span" sx={{ color: c.ink3, mr: '2px' }}>
                        <FormattedMessage id="app.inquiries.detail.positionGroups" />
                    </Box>
                    {shownGroups.map(group => (
                        <Box
                            key={group.name}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 4px 2px 10px',
                                border: `1px solid ${c.line}`,
                                borderRadius: '999px',
                                backgroundColor: c.surface,
                                color: c.ink2,
                                maxWidth: 340,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                                {group.name}
                            </Box>
                            {group.ids.length > 1 && (
                                <Box component="span" sx={{ color: c.ink3, whiteSpace: 'nowrap' }}>
                                    <FormattedMessage
                                        id="app.inquiries.detail.groupMerged"
                                        values={{ count: group.ids.length }}
                                    />
                                </Box>
                            )}
                            <ButtonBase
                                aria-label={intl.formatMessage({ id: 'app.common.remove' })}
                                onClick={() => removeGroup(group)}
                                sx={{ borderRadius: '50%', color: c.ink3, '&:hover': { color: c.ink } }}
                            >
                                <CloseOutlined sx={{ fontSize: 14 }} />
                            </ButtonBase>
                        </Box>
                    ))}
                    {groups.length > VISIBLE_CHIPS && (
                        <ButtonBase
                            onClick={() => setAllChips(value => !value)}
                            sx={{ color: c.accentHi, fontSize: '12px', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {allChips ? (
                                <FormattedMessage id="app.inquiries.detail.groupsLess" />
                            ) : (
                                `+${groups.length - VISIBLE_CHIPS}`
                            )}
                        </ButtonBase>
                    )}
                </Box>
            )}

            {isDistillate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', fontSize: '12px' }}>
                    <Box component="span" sx={{ color: c.ink3 }}>
                        <FormattedMessage
                            id="app.inquiries.detail.distillateRegions"
                            values={{
                                regions: unique(
                                    deliveryRegions.map(({ region }) => region),
                                    r => r
                                ).join(', '),
                            }}
                        />
                    </Box>
                    {deliveryRegions.length > 1 && (
                        <FormControlLabel
                            sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12px' } }}
                            control={
                                <Switch
                                    size="small"
                                    checked={partialRegions}
                                    onChange={() => toggleRegions({ partialRegions: !partialRegions })}
                                />
                            }
                            label={<FormattedMessage id="app.inquiries.detail.distillateRegionsPartial" />}
                        />
                    )}
                    <FormControlLabel
                        sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12px' } }}
                        control={
                            <Switch
                                size="small"
                                checked={withoutRegion}
                                onChange={() => toggleRegions({ withoutRegion: !withoutRegion })}
                            />
                        }
                        label={<FormattedMessage id="app.inquiries.detail.distillateWithoutRegion" />}
                    />
                </Box>
            )}

            <PoolModal
                isVisible={treeOpen}
                title={<FormattedMessage id="app.filter.filterTitle" />}
                handleClose={() => {
                    setTreeOpen(false);
                    searchNow.current(selectedIds, search, contractPartnersOnly);
                }}
                actionChildren={[
                    <PoolButton key="reset" variant="text" styleType="firstLevel" onClick={() => setSelectedIds([])}>
                        <FormattedMessage id="app.filter.reset" />
                    </PoolButton>,
                    <PoolButton
                        key="show"
                        styleType="firstLevel"
                        onClick={() => {
                            setTreeOpen(false);
                            searchNow.current(selectedIds, search, contractPartnersOnly);
                        }}
                    >
                        <FormattedMessage id="app.filter.showResults" />
                    </PoolButton>,
                ]}
                width="lg"
            >
                <PoolCategoryTree selectedIds={selectedIds} onChange={setSelectedIds} />
            </PoolModal>
        </Box>
    );
};
